import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../../i18n/contentLocales';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteStation, getStation, updateStation } from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { MultilingualString, Station, StationTips } from '../../api/types';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import MultilingualField from '../../components/MultilingualField';
import StationImageField from '../../components/StationImageField';
import StationTipsField from '../../components/StationTipsField';
import BackLink from '../../components/BackLink';
import MapPicker from '../../components/MapPicker';

const StationDetail: React.FC = () => {
  const { stationId } = useParams<{ stationId: string }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('content');
  const navigate = useNavigate();

  const [station, setStation] = useState<Station | null>(null);
  const [title, setTitle] = useState<MultilingualString>({});
  const [description, setDescription] = useState<MultilingualString>({});
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [address, setAddress] = useState('');
  const [locationPending, setLocationPending] = useState(false);
  const [tips, setTips] = useState<StationTips | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const orgSlug = user?.organization_slug;

  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);

  const load = useCallback(async () => {
    if (!orgSlug || !stationId) return;
    setLoading(true);
    setError(null);
    try {
      const s = await getStation(orgSlug, stationId);
      setStation(s);
      setTitle(s.title);
      setDescription(s.description);
      setLocationLat(s.location_lat);
      setLocationLng(s.location_lng);
      setAddress(s.address ?? '');
      setTips(s.tips);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, stationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const usageCount = station?.usage?.rallye_ids.length ?? 0;
  const isDirty =
    !!station &&
    (JSON.stringify(title) !== JSON.stringify(station.title) ||
      JSON.stringify(description) !== JSON.stringify(station.description) ||
      (station.address ?? '') !== address ||
      station.location_lat !== locationLat ||
      station.location_lng !== locationLng ||
      JSON.stringify(station.tips) !== JSON.stringify(tips));
  const deTitleMissing = !title.de?.trim();
  // An address without a map position is the wrong-house bug (#547).
  const coordinatesMissing = !!address.trim() && (locationLat === null || locationLng === null);
  const locationBlocked = locationPending || coordinatesMissing;

  const handleSave = async () => {
    if (!orgSlug || !stationId || !station || deTitleMissing || locationBlocked) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateStation(orgSlug, stationId, {
        title,
        description,
        address: address || null,
        location_lat: locationLat,
        location_lng: locationLng,
        // Always send tips (even null) — the backend treats explicit null as
        // "clear", while omitting the field would leave it unchanged.
        tips,
      });
      setStation(updated);
      setAddress(updated.address ?? '');
      setTips(updated.tips);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!orgSlug || !stationId) return;
    setDeleting(true);
    try {
      await deleteStation(orgSlug, stationId);
      navigate('/stations');
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!station) {
    return (
      <Box>
        <ErrorAlert message={error ?? 'Station not found'} onRetry={load} />
      </Box>
    );
  }

  const rallyeNames = (station.usage?.rallye_titles ?? []).map((rt) => titleOf(rt)).filter(Boolean);

  return (
    <Box>
      <BackLink to="/stations" label={t('station.detail.backToList')} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3 }}
      >
        <Typography variant="h2" component="h1">
          {titleOf(station.title) || t('station.detail.title')}
        </Typography>
        <Button
          startIcon={<DeleteIcon />}
          color="error"
          variant="outlined"
          onClick={() => setDeleteOpen(true)}
        >
          {t('station.detail.delete')}
        </Button>
      </Stack>

      {error && <ErrorAlert message={error} />}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('station.detail.editTitle')}
          </Typography>
          {usageCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('station.detail.usedInTitle', { count: usageCount })}
              {rallyeNames.length > 0 && `: ${rallyeNames.join(', ')}`}
              {`. ${t('station.detail.propagationNote')}`}
            </Alert>
          )}
          <Stack spacing={3}>
            <MultilingualField
              value={title}
              onChange={setTitle}
              label={t('station.create.titleLabel')}
              required
            />
            <MultilingualField
              value={description}
              onChange={setDescription}
              label={t('station.create.descriptionLabel')}
              richText
            />
            <Box>
              <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                {t('station.create.locationLabel')}
              </Typography>
              <MapPicker
                lat={locationLat}
                lng={locationLng}
                address={address}
                onChange={({ lat, lng, address: nextAddress }) => {
                  setLocationLat(lat);
                  setLocationLng(lng);
                  setAddress(nextAddress);
                }}
                disabled={saving}
                searchLabel={t('station.create.addressLabel')}
                onPendingChange={setLocationPending}
              />
              {locationBlocked && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }} role="alert">
                  {t(locationPending ? 'station.location.searchPending' : 'station.location.coordinatesRequired')}
                </Typography>
              )}
            </Box>
            <StationImageField
              orgSlug={orgSlug!}
              stationId={stationId!}
              imageUrl={station.title_image_url}
              onChange={setStation}
            />
            <StationTipsField value={tips} onChange={setTips} disabled={saving} />
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isDirty || saving || deTitleMissing || locationBlocked}
              >
                {usageCount > 0
                  ? t('station.detail.saveAcrossAll')
                  : t('station.detail.save')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        title={t('station.detail.delete')}
        message={
          rallyeNames.length > 0
            ? t('station.detail.deleteInUseConfirm', {
                count: rallyeNames.length,
                rallyes: rallyeNames.join(', '),
              })
            : t('station.detail.deleteConfirm')
        }
        destructive
        loading={deleting}
        confirmLabel={t('actions.delete', { ns: 'common' })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default StationDetail;
